import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  useGetTransactionsQuery,
  useGetTransactionStatsQuery,
  useProcessRefundMutation,
} from '../../../store/api/adminApi';
import PageLoader from '../../../components/PageLoader';
import Pagination from '../../../components/Pagination';
import DateRangePicker from '../../../components/DateRangePicker';
import { showSuccess, showError } from '../../../utils/toast';
import Swal from 'sweetalert2';
import env from '../../../config/env';

const formatDate = (d: string | null | undefined) => {
  if (!d) return '-';
  const dt = new Date(d);
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${dt.getDate().toString().padStart(2,'0')} ${months[dt.getMonth()]}, ${dt.getFullYear()} ${dt.getHours().toString().padStart(2,'0')}:${dt.getMinutes().toString().padStart(2,'0')}`;
};

const TYPE_LABELS: Record<string, { label: string; cls: string }> = {
  subscription:  { label: 'Subscription',   cls: 'badge-light-primary' },
  ad_payment:    { label: 'Ad Payment',      cls: 'badge-light-info'    },
  wallet_credit: { label: 'Wallet Credit',   cls: 'badge-light-success' },
  wallet_debit:  { label: 'Wallet Debit',    cls: 'badge-light-warning' },
  voucher:       { label: 'Voucher',         cls: 'badge-light-secondary'},
  refund:        { label: 'Refund',          cls: 'badge-light-danger'  },
};

const STATUS_CLS: Record<string, string> = {
  completed: 'badge-light-success',
  pending:   'badge-light-warning',
  failed:    'badge-light-danger',
  refunded:  'badge-light-secondary',
};

const AdminFinance: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [search,        setSearch]        = useState(searchParams.get('search')         || '');
  const [type,          setType]          = useState(searchParams.get('type')           || '');
  const [status,        setStatus]        = useState(searchParams.get('status')         || '');
  const [paymentMethod, setPaymentMethod] = useState(searchParams.get('payment_method') || '');
  const [daterange,     setDaterange]     = useState(searchParams.get('daterange')      || '');
  const [page,          setPage]          = useState(Number(searchParams.get('page'))   || 1);
  const [expandedRow,   setExpandedRow]   = useState<number | null>(null);

  const params: Record<string, any> = { page };
  if (search)        params.search         = search;
  if (type)          params.type           = type;
  if (status)        params.status         = status;
  if (paymentMethod) params.payment_method = paymentMethod;
  if (daterange)     params.daterange      = daterange;

  const { data, isLoading }       = useGetTransactionsQuery(params);
  const { data: statsData }       = useGetTransactionStatsQuery(daterange ? { daterange } : {});
  const [processRefund, { isLoading: isRefunding }] = useProcessRefundMutation();

  useEffect(() => {
    const sp: Record<string, string> = {};
    if (search)        sp.search         = search;
    if (type)          sp.type           = type;
    if (status)        sp.status         = status;
    if (paymentMethod) sp.payment_method = paymentMethod;
    if (daterange)     sp.daterange      = daterange;
    if (page > 1)      sp.page           = String(page);
    setSearchParams(sp, { replace: true });
  }, [search, type, status, paymentMethod, daterange, page]);

  const resetPage = () => setPage(1);
  const clearAll  = () => {
    setSearch(''); setType(''); setStatus(''); setPaymentMethod('');
    setDaterange(''); setPage(1);
  };
  const hasFilters = search || type || status || paymentMethod || daterange;

  const handleRefund = async (txn: any) => {
    const { value: reason, isConfirmed } = await Swal.fire({
      title: 'Process Refund',
      html: `<p class="text-muted mb-3">Refunding <strong>AED ${Number(txn.amount).toFixed(2)}</strong> for <strong>${txn.user?.name || '-'}</strong></p>`,
      input: 'textarea',
      inputLabel: 'Refund Reason (optional)',
      inputPlaceholder: 'Enter reason for refund…',
      inputAttributes: { rows: '3' },
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Process Refund',
    });
    if (!isConfirmed) return;

    try {
      await processRefund({ id: txn.id, reason: reason || '' }).unwrap();
      showSuccess('Refund processed successfully.');
    } catch (err: any) {
      showError(err?.data?.message || 'Failed to process refund.');
    }
  };

  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    const base = env.apiUrl.replace(/\/$/, '');
    const token = localStorage.getItem('token') || '';
    const qp = new URLSearchParams();
    if (type)          qp.set('type',           type);
    if (status)        qp.set('status',         status);
    if (paymentMethod) qp.set('payment_method', paymentMethod);
    if (daterange)     qp.set('daterange',       daterange);

    setIsExporting(true);
    try {
      const res = await fetch(`${base}/admin/transactions/export?${qp.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'text/csv' },
      });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `transactions-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch {
      showError('Failed to export. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const stats  = statsData?.data;
  const txns   = data?.data?.data || [];
  const meta   = data?.data || { current_page: 1, last_page: 1, total: 0, per_page: 20 };
  const trend  = stats?.monthly_trend || [];

  if (isLoading && !data) return <PageLoader />;

  return (
    <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
      <div className="d-flex flex-column flex-column-fluid">

        {/* Toolbar */}
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
          <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                Finance &amp; Payments
              </h1>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted"><span className="text-muted">Home</span></li>
                <li className="breadcrumb-item"><span className="bullet bg-gray-400 w-5px h-2px"></span></li>
                <li className="breadcrumb-item text-muted">Finance &amp; Payments</li>
              </ul>
            </div>
          </div>
        </div>

        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div id="kt_app_content_container" className="app-container container-xxl">

            {/* Stat Cards */}
            {stats && (
              <div className="row g-5 mb-6">
                {[
                  {
                    label: 'Total Revenue',
                    value: `AED ${Number(stats.total_revenue).toLocaleString('en', { minimumFractionDigits: 2 })}`,
                    sub: `${stats.completed_count} completed`,
                    icon: 'fas fa-coins',
                    color: 'success',
                  },
                  {
                    label: 'Pending Payments',
                    value: `AED ${Number(stats.pending_payments).toLocaleString('en', { minimumFractionDigits: 2 })}`,
                    sub: `${stats.pending_count} pending`,
                    icon: 'fas fa-clock',
                    color: 'warning',
                  },
                  {
                    label: 'Total Refunded',
                    value: `AED ${Number(stats.total_refunded).toLocaleString('en', { minimumFractionDigits: 2 })}`,
                    sub: `${stats.refunded_count} refunds`,
                    icon: 'fas fa-undo',
                    color: 'danger',
                  },
                  {
                    label: 'Wallet Credits',
                    value: `AED ${Number(stats.wallet_credits).toLocaleString('en', { minimumFractionDigits: 2 })}`,
                    sub: `${stats.total_count} total transactions`,
                    icon: 'fas fa-wallet',
                    color: 'info',
                  },
                ].map((s) => (
                  <div key={s.label} className="col-6 col-xl-3">
                    <div className="card h-100">
                      <div className="card-body d-flex align-items-center gap-4 py-6">
                        <div className={`symbol symbol-55px symbol-light-${s.color} flex-shrink-0`}>
                          <span className={`symbol-label bg-light-${s.color}`}>
                            <i className={`${s.icon} fs-2 text-${s.color}`}></i>
                          </span>
                        </div>
                        <div>
                          <div className="fw-bold fs-3 text-gray-800">{s.value}</div>
                          <div className="text-muted fs-7">{s.label}</div>
                          <div className="text-muted fs-8">{s.sub}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Monthly Revenue Trend */}
            {trend.length > 0 && (
              <div className="card mb-6">
                <div className="card-header border-0 pt-6">
                  <h3 className="card-title fw-bold fs-4">Monthly Revenue (Last 6 Months)</h3>
                </div>
                <div className="card-body pt-2">
                  <div className="d-flex align-items-end gap-3" style={{ height: 120 }}>
                    {(() => {
                      const max = Math.max(...trend.map((t: any) => t.revenue), 1);
                      return trend.map((t: any) => (
                        <div key={t.month} className="d-flex flex-column align-items-center flex-fill gap-1">
                          <span className="text-muted fs-9 fw-bold">{Number(t.revenue).toFixed(0)}</span>
                          <div
                            className="rounded w-100"
                            style={{
                              height: `${Math.max(6, (t.revenue / max) * 90)}px`,
                              background: '#396A54',
                              opacity: 0.85,
                              minWidth: 30,
                            }}
                          />
                          <span className="text-muted fs-9">{t.month}</span>
                        </div>
                      ));
                    })()}
                  </div>
                </div>
              </div>
            )}

            {/* Transactions Table */}
            <div className="card">
              <div className="card-header border-0 pt-5 pb-3">
                <div className="d-flex align-items-center flex-wrap gap-2 w-100">

                  {/* Search */}
                  <div className="d-flex align-items-center position-relative" style={{ minWidth: 200, flex: '1 1 200px', maxWidth: 280 }}>
                    <span className="svg-icon svg-icon-1 position-absolute ms-4">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <rect opacity="0.5" x="17.0365" y="15.1223" width="8.15546" height="2" rx="1" transform="rotate(45 17.0365 15.1223)" fill="currentColor"/>
                        <path d="M11 19C6.55556 19 3 15.4444 3 11C3 6.55556 6.55556 3 11 3C15.4444 3 19 6.55556 19 11C19 15.4444 15.4444 19 11 19ZM11 5C7.53333 5 5 7.53333 5 11C5 14.4667 7.53333 17 11 17C14.4667 17 17 14.4667 17 11C17 7.53333 14.4667 5 11 5Z" fill="currentColor"/>
                      </svg>
                    </span>
                    <input
                      type="text"
                      className="form-control form-control-solid ps-13"
                      placeholder="Search user, email…"
                      value={search}
                      onChange={(e) => { setSearch(e.target.value); resetPage(); }}
                    />
                  </div>

                  {/* Type */}
                  <select
                    className="form-select form-select-solid"
                    style={{ width: 148, flex: '0 0 148px' }}
                    value={type}
                    onChange={(e) => { setType(e.target.value); resetPage(); }}
                  >
                    <option value="">All Types</option>
                    <option value="subscription">Subscription</option>
                    <option value="ad_payment">Ad Payment</option>
                    <option value="wallet_credit">Wallet Credit</option>
                    <option value="wallet_debit">Wallet Debit</option>
                    <option value="voucher">Voucher</option>
                    <option value="refund">Refund</option>
                  </select>

                  {/* Status */}
                  <select
                    className="form-select form-select-solid"
                    style={{ width: 140, flex: '0 0 140px' }}
                    value={status}
                    onChange={(e) => { setStatus(e.target.value); resetPage(); }}
                  >
                    <option value="">All Statuses</option>
                    <option value="completed">Completed</option>
                    <option value="pending">Pending</option>
                    <option value="failed">Failed</option>
                    <option value="refunded">Refunded</option>
                  </select>

                  {/* Payment Method */}
                  <select
                    className="form-select form-select-solid"
                    style={{ width: 140, flex: '0 0 140px' }}
                    value={paymentMethod}
                    onChange={(e) => { setPaymentMethod(e.target.value); resetPage(); }}
                  >
                    <option value="">All Methods</option>
                    <option value="stripe">Stripe</option>
                    <option value="wallet">Wallet</option>
                    <option value="voucher">Voucher</option>
                    <option value="manual">Manual</option>
                  </select>

                  {/* Date Range */}
                  <div style={{ flex: '0 0 230px' }}>
                    <DateRangePicker
                      value={daterange}
                      onChange={(val) => { setDaterange(val); resetPage(); }}
                      placeholder="Date Range"
                    />
                  </div>

                  {/* Clear */}
                  {hasFilters && (
                    <button className="btn btn-sm btn-light-danger" onClick={clearAll}>
                      <i className="fas fa-times me-1"></i>Clear
                    </button>
                  )}

                  {/* Export — pushed to the right */}
                  <div className="ms-auto">
                    <button className="btn btn-sm btn-light-success" onClick={handleExport} disabled={isExporting}>
                      {isExporting
                        ? <><span className="spinner-border spinner-border-sm me-2" />Exporting…</>
                        : <><i className="fas fa-download me-2"></i>Export CSV</>
                      }
                    </button>
                  </div>
                </div>
              </div>

              <div className="card-body pt-0">
                <div className="d-flex align-items-center mb-3 mt-4">
                  <span className="text-muted fs-7">
                    Total: <strong>{meta.total || 0}</strong> records
                  </span>
                </div>

                <div className="table-responsive">
                  <table className="table align-middle table-row-dashed fs-6 gy-4">
                    <thead>
                      <tr className="text-start text-muted fw-bold fs-7 text-uppercase gs-0">
                        <th className="min-w-40px">#</th>
                        <th className="min-w-120px">Customer</th>
                        <th className="min-w-100px">Type</th>
                        <th className="min-w-100px">Amount</th>
                        <th className="min-w-80px">Method</th>
                        <th className="min-w-90px">Status</th>
                        <th className="min-w-120px">Date</th>
                        <th className="min-w-100px">Refund Info</th>
                        <th className="min-w-80px">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="text-gray-600 fw-semibold">
                      {txns.length === 0 ? (
                        <tr>
                          <td colSpan={9} className="text-center text-muted py-10">No transactions found</td>
                        </tr>
                      ) : (
                        txns.map((txn: any) => (
                          <React.Fragment key={txn.id}>
                            <tr>
                              <td className="text-muted fs-7">{txn.id}</td>
                              <td>
                                <div className="fw-bold text-gray-800">{txn.user?.name || '-'}</div>
                                <div className="text-muted fs-7">{txn.user?.email || ''}</div>
                              </td>
                              <td>
                                <span className={`badge ${TYPE_LABELS[txn.type]?.cls || 'badge-light-secondary'}`}>
                                  {TYPE_LABELS[txn.type]?.label || txn.type}
                                </span>
                              </td>
                              <td>
                                <span className={`fw-bold ${txn.type === 'refund' || txn.type === 'wallet_debit' ? 'text-danger' : 'text-success'}`}>
                                  {txn.type === 'refund' || txn.type === 'wallet_debit' ? '-' : '+'}
                                  {txn.currency} {Number(txn.amount).toFixed(2)}
                                </span>
                              </td>
                              <td>
                                <span className="text-muted text-capitalize">{txn.payment_method || '-'}</span>
                              </td>
                              <td>
                                <span className={`badge ${STATUS_CLS[txn.status] || 'badge-light-secondary'}`}>
                                  {txn.status}
                                </span>
                              </td>
                              <td className="text-muted fs-7">{formatDate(txn.created_at)}</td>
                              <td>
                                {txn.status === 'refunded' ? (
                                  <button
                                    className="btn btn-xs btn-light-secondary"
                                    onClick={() => setExpandedRow(expandedRow === txn.id ? null : txn.id)}
                                  >
                                    {expandedRow === txn.id ? 'Hide' : 'Details'}
                                  </button>
                                ) : (
                                  <span className="text-muted fs-8">—</span>
                                )}
                              </td>
                              <td>
                                {txn.status === 'completed' && txn.type !== 'refund' && (
                                  <button
                                    className="btn btn-sm btn-light-danger"
                                    onClick={() => handleRefund(txn)}
                                    disabled={isRefunding}
                                    title="Process Refund"
                                  >
                                    <i className="fas fa-undo me-1"></i>Refund
                                  </button>
                                )}
                              </td>
                            </tr>

                            {/* Refund Detail Row */}
                            {expandedRow === txn.id && txn.status === 'refunded' && (
                              <tr>
                                <td colSpan={9} className="bg-light-danger p-0">
                                  <div className="d-flex flex-wrap gap-8 px-6 py-4">
                                    <div>
                                      <div className="text-muted fs-8 mb-1">Refunded By</div>
                                      <div className="fw-bold text-gray-800 fs-7">
                                        {txn.refunded_by_user?.name || '-'}
                                      </div>
                                    </div>
                                    <div>
                                      <div className="text-muted fs-8 mb-1">Refund Reason</div>
                                      <div className="fw-bold text-gray-800 fs-7">
                                        {txn.refund_reason || 'No reason provided'}
                                      </div>
                                    </div>
                                    <div>
                                      <div className="text-muted fs-8 mb-1">Refunded At</div>
                                      <div className="fw-bold text-gray-800 fs-7">
                                        {formatDate(txn.refunded_at)}
                                      </div>
                                    </div>
                                    {txn.stripe_payment_id && (
                                      <div>
                                        <div className="text-muted fs-8 mb-1">Stripe Payment ID</div>
                                        <div className="fw-bold text-gray-800 fs-7 font-monospace">
                                          {txn.stripe_payment_id}
                                        </div>
                                      </div>
                                    )}
                                    {txn.notes && (
                                      <div>
                                        <div className="text-muted fs-8 mb-1">Notes</div>
                                        <div className="fw-bold text-gray-800 fs-7">{txn.notes}</div>
                                      </div>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                <Pagination
                  currentPage={meta.current_page}
                  lastPage={meta.last_page}
                  total={meta.total || 0}
                  onPageChange={(p) => { setPage(p); window.scrollTo(0, 0); }}
                />
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminFinance;
